import React, { useCallback, useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { makeStyles } from '@material-ui/core/styles'
import RoundedModal from '../../../../molecules/RoundedModal'
import SydButton from '../../../../commonDesign/Button'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import Loading from '../../../../molecules/Loading'
import { useGetDagStatus } from '../../../../../api/dags'

const useStyles = makeStyles({
  warning: {
    backgroundColor: '#FFF3E0',
    border: '1px solid #FFB74D',
    borderRadius: 6,
    padding: '12px 16px',
    fontSize: 13,
    color: '#E65100',
    marginBottom: 16
  }
})

const RunDagDialog = React.forwardRef(function RunDagDialog ({ onConfirm }, ref) {
  const classes = useStyles()
  const [dialogState, setDialogState] = useState({ open: false, processing: false, dag: null })

  const dagId = dialogState.dag?.dagId
  const { data: dagStatus, isLoading: isCheckingStatus } = useGetDagStatus(dagId, {
    enabled: dialogState.open && !!dagId
  })

  const isRunning = dagStatus?.status === 'running'

  const close = useCallback(() => {
    setDialogState({ open: false, processing: false, dag: null })
  }, [])

  const handleConfirm = useCallback(async () => {
    try {
      setDialogState((prev) => ({ ...prev, processing: true }))
      await onConfirm(dialogState.dag)
    } finally {
      close()
    }
  }, [dialogState.dag, onConfirm, close])

  useImperativeHandle(ref, () => ({
    open: (dag) => {
      setDialogState({ open: true, processing: false, dag })
    }
  }), [])

  return (
    <RoundedModal
      title='Run Workflow'
      open={dialogState.open}
      onClose={close}
      size='medium'
      position='top'
      disablePortal={false}
    >
      <div>
        {dialogState.open ? (
          <>
            {isCheckingStatus ? (
              <Loading />
            ) : isRunning ? (
              <>
                <div className={classes.warning}>
                  This workflow is currently running
                  {dagStatus.when
                    ? ` (started ${dayjs(dagStatus.when).format('MMM D, YYYY h:mm A')})`
                    : ''}.
                  Please wait for it to finish before starting a new run.
                </div>
                <SydModalActions>
                  <SydButton variant='ghost' size='md' onClick={close}>
                    Close
                  </SydButton>
                </SydModalActions>
              </>
            ) : (
              <>
                <p style={{ margin: '0 0 16px' }}>
                  Run workflow <strong>{dialogState.dag?.name}</strong>?
                  This will trigger the first step of the DAG.
                </p>
                {/* Future: execution arguments form */}
                <SydModalActions>
                  <SydButton disabled={dialogState.processing} variant='ghost' size='md' onClick={close}>
                    Cancel
                  </SydButton>
                  <SydButton
                    processing={dialogState.processing}
                    variant='primary'
                    icon='play_arrow'
                    size='md'
                    onClick={handleConfirm}
                  >
                    Run
                  </SydButton>
                </SydModalActions>
              </>
            )}
          </>
        ) : null}
      </div>
    </RoundedModal>
  )
})

RunDagDialog.propTypes = {
  onConfirm: PropTypes.func.isRequired
}

export default RunDagDialog
